import { useState } from "react";
import { ChevronDown } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/modern/components/ui/popover";
import { cn } from "@/modern/utils";

import { SURFACE_CONTROL_CLS } from "../constants/layout";
import {
  UPDATES_HISTORY_PRESETS,
  historyRangeStatusLabel,
  type UpdatesHistoryRange,
  type UpdatesInboxMode,
} from "../utils/timeWindow";

const PRESET_BTN_CLS =
  "text-left text-sm px-2.5 py-1.5 rounded-md transition-colors";

const SEEN_SEGMENT_CLS =
  "h-8 rounded-full px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function NotificationWindowChrome({
  inboxMode,
  onInboxModeChange,
  historyRange,
  onHistoryRangeChange,
  className,
}: {
  inboxMode: UpdatesInboxMode;
  onInboxModeChange: (next: UpdatesInboxMode) => void;
  historyRange: UpdatesHistoryRange;
  onHistoryRangeChange: (next: UpdatesHistoryRange) => void;
  className?: string;
}) {
  return (
    <div className={cn("flex shrink-0 items-center gap-2", className)}>
      <NotificationInboxModeToggle
        selected={inboxMode}
        onSelect={onInboxModeChange}
      />
      {inboxMode === "all" ? (
        <NotificationHistoryRangeControl
          value={historyRange}
          onChange={onHistoryRangeChange}
        />
      ) : null}
    </div>
  );
}

function NotificationInboxModeToggle({
  selected,
  onSelect,
}: {
  selected: UpdatesInboxMode;
  onSelect: (mode: UpdatesInboxMode) => void;
}) {
  return (
    <div
      className={cn(
        "inline-flex h-9 items-center rounded-full p-0.5",
        SURFACE_CONTROL_CLS,
      )}
      role="group"
      aria-label="Inbox mode"
    >
      {(["unread", "all"] as const).map((mode) => {
        const isSelected = selected === mode;
        return (
          <button
            key={mode}
            type="button"
            aria-pressed={isSelected}
            onClick={() => onSelect(mode)}
            className={cn(
              SEEN_SEGMENT_CLS,
              isSelected
                ? "bg-background text-foreground shadow-sm"
                : "text-foreground/70 hover:text-foreground",
            )}
          >
            {mode === "unread" ? "Unread" : "All"}
          </button>
        );
      })}
    </div>
  );
}

function NotificationHistoryRangeControl({
  value,
  onChange,
}: {
  value: UpdatesHistoryRange;
  onChange: (next: UpdatesHistoryRange) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex h-9 max-w-full items-center gap-1 rounded-full px-3",
            SURFACE_CONTROL_CLS,
            "text-sm font-medium text-foreground/80",
            "hover:border-input-enhanced hover:text-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
          aria-label={`Show updates from ${historyRangeStatusLabel(value)}`}
        >
          <span className="min-w-0 truncate">{historyRangeStatusLabel(value)}</span>
          <ChevronDown
            className="size-3.5 shrink-0 opacity-60 transition-transform [[data-state=open]_&]:rotate-180"
            aria-hidden
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="z-[--swivu-bryntum-z-index] w-auto p-0"
      >
        <div className="flex min-w-[8.5rem] flex-col gap-0.5 p-2">
          <p className="px-2 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground/70">
            Show from
          </p>
          {UPDATES_HISTORY_PRESETS.map((preset) => {
            const active = value.kind === preset.kind;
            return (
              <button
                key={preset.kind}
                type="button"
                onClick={() => {
                  onChange({ kind: preset.kind });
                  setOpen(false);
                }}
                className={cn(
                  PRESET_BTN_CLS,
                  active
                    ? "bg-muted font-medium text-foreground"
                    : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                )}
              >
                {preset.label}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
